<template>
  <div
    class="hero-grass-fringe"
    :class="{ 'is-still': heroState.reducedMotion, 'is-settled': settled }"
    aria-hidden="true"
  >
    <!-- Foreground grass in front of the rabbit: same parallax depth as the
         meadow (overscanned), rising with it on entrance. The tufts are
         generated for the real width and sway in the shared wind. The SVG
         takes the theme from its tone class and crossfades at dusk and dawn
         (see crossfade). -->
    <div class="hgf-par">
      <div class="hgf-rise">
        <svg ref="svg" class="hgf-svg" :class="'hgf-tone-' + tone" preserveAspectRatio="xMidYMax slice" focusable="false"></svg>
      </div>
    </div>
  </div>
</template>

<script>
import { createLoop, exposeDebug, removeDebug } from './heroMotion';
import { gustField, breezeAt } from './HeroLandscapeWind';
import { hash, noise } from './HeroLandscapeScene';

// px of horizontal overscan on each side (.hgf-par).
const OVERSCAN = 24;
// s of clock after which the entrance rise is over (see .is-settled).
const SETTLED_AT = 2;
// The tufts' tips move a fraction of a pixel per step: ~30 redraws a second
// are plenty, in step with the landscape's trees.
const STEADY_FRAME = 1 / 31;
// Dusk and dawn, in step with the sky and the landscape (HeroSky.vue,
// HeroLandscape.vue): the land follows the sky by a beat at dusk.
const FADE_MS = 1400;
const FADE_EASE = 'cubic-bezier(0.45, 0.05, 0.55, 0.95)';
const FADE_DELAY = { night: 200, day: 100 };
// Snapshots kept if the theme is toggled again and again.
const MAX_GHOSTS = 3;

function f2(v) {
  return String(Math.round(v * 100) / 100);
}

function circle(cx, cy, r) {
  return `M${f2(cx - r)},${f2(cy)}a${f2(r)},${f2(r)} 0 1,0 ${f2(2 * r)},0a${f2(r)},${f2(r)} 0 1,0 ${f2(-2 * r)},0`;
}

// A tapered, slightly curved blade rooted just below the bottom edge
// (y = 0 is the bottom of the hero, up is negative).
function blade(x, h, lean, w) {
  return `M${f2(x - w / 2)},2Q${f2(x - w * 0.1 + lean * 0.3)},${f2(-h * 0.55)} ${f2(x + lean)},${f2(-h)}` +
    `Q${f2(x + w * 0.25 + lean * 0.45)},${f2(-h * 0.5)} ${f2(x + w / 2)},2Z`;
}

function tone(v) {
  return v < 0.35 ? 'hgf-a' : (v < 0.8 ? 'hgf-b' : 'hgf-c');
}

// A fan of blades around x = 0 (the tuft's own origin), one path per tone:
// every blade winds the same way, so each path is the union of its blades,
// darker tones behind the lighter ones.
function tuftMarkup(k, seed, count, height, spread, bladeW) {
  const paths = { 'hgf-a': '', 'hgf-b': '', 'hgf-c': '' };
  for (let j = 0; j < count; j++) {
    const f = count > 1 ? j / (count - 1) - 0.5 : 0;
    const bx = f * spread + (hash(k * 8 + j, seed) - 0.5) * spread * 0.25;
    const bh = height * (0.6 + 0.4 * hash(k * 8 + j, seed + 1)) * (1 - Math.abs(f) * 0.35);
    const lean = f * height * 0.55 + (hash(k * 8 + j, seed + 2) - 0.5) * height * 0.25;
    paths[tone(hash(k * 8 + j, seed + 3))] += blade(bx, bh, lean, bladeW);
  }
  let s = '';
  Object.keys(paths).forEach((cls) => {
    if (paths[cls]) s += `<path class="${cls}" d="${paths[cls]}"/>`;
  });
  return s;
}

// Every tuft between x0 and x1 (px from the hero's centre). H is the fringe
// height; the rabbit's feet rest at about 0.49 H, so the low carpet stays
// below them and only the sparse taller tufts reach their lower part.
function buildTufts(x0, x1, H) {
  const tufts = [];
  const bladeW = Math.min(Math.max(H * 0.065, 1.6), 2.8);

  let k0 = Math.floor(x0 / 10) - 1;
  let k1 = Math.ceil(x1 / 10) + 1;
  for (let k = k0; k <= k1; k++) {
    const x = (k + 0.5 + (hash(k, 1201) - 0.5) * 0.8) * 10;
    const clump = noise(x / 70, 1203);
    if (hash(k, 1205) > 0.35 + 0.6 * clump) continue;
    const height = H * (0.18 + 0.26 * hash(k, 1207)) * (0.75 + 0.5 * clump);
    const count = 3 + Math.floor(hash(k, 1209) * 3);
    tufts.push({
      x,
      h: height,
      markup: tuftMarkup(k, 1211, count, height, height * 0.55, bladeW),
      amp: 2 + hash(k, 1213) * 1.2,
      period: 2.8 + hash(k, 1215) * 1.8,
      phase: hash(k, 1217) * Math.PI * 2,
      gustAmp: 6
    });
  }

  k0 = Math.floor(x0 / 46) - 1;
  k1 = Math.ceil(x1 / 46) + 1;
  for (let k = k0; k <= k1; k++) {
    if (hash(k, 1301) > 0.62) continue;
    const x = (k + 0.5 + (hash(k, 1303) - 0.5) * 0.7) * 46;
    const tall = hash(k, 1305) < 0.12;
    const height = H * (tall ? 0.84 + hash(k, 1307) * 0.14 : 0.5 + hash(k, 1307) * 0.3);
    const count = 5 + Math.floor(hash(k, 1309) * 4);
    tufts.push({
      x,
      h: height,
      markup: tuftMarkup(k, 1311, count, height, height * 0.5, bladeW * 1.1),
      amp: 2.8 + hash(k, 1313) * 1.4,
      period: 3 + hash(k, 1315) * 2,
      phase: hash(k, 1317) * Math.PI * 2,
      gustAmp: 10
    });
  }

  // By day: a few tiny flowers on thin stems and clover leaves.
  const petal = Math.min(Math.max(H * 0.05, 1.3), 2.1);
  const colours = ['hgf-fl-w', 'hgf-fl-y', 'hgf-fl-p'];
  k0 = Math.floor(x0 / 120) - 1;
  k1 = Math.ceil(x1 / 120) + 1;
  for (let k = k0; k <= k1; k++) {
    if (hash(k, 1401) > 0.6) continue;
    const x = (k + 0.5 + (hash(k, 1403) - 0.5) * 0.8) * 120;
    const h = H * (0.5 + hash(k, 1405) * 0.38);
    const lean = (hash(k, 1407) - 0.5) * H * 0.2;
    const colour = colours[Math.floor(hash(k, 1409) * 2.999)];
    let head = '';
    const rot = hash(k, 1411) * 6.28;
    for (let j = 0; j < 5; j++) {
      const a = rot + (j * Math.PI * 2) / 5;
      head += circle(lean + Math.cos(a) * petal * 1.15, -h + Math.sin(a) * petal * 1.15, petal);
    }
    tufts.push({
      x,
      h,
      markup: '<g class="hgf-day">' +
        `<path class="hgf-stem" d="M-0.5,2Q${f2(lean * 0.2)},${f2(-h * 0.5)} ${f2(lean - 0.4)},${f2(-h)}` +
        `L${f2(lean + 0.4)},${f2(-h)}Q${f2(lean * 0.2 + 1)},${f2(-h * 0.5)} 0.5,2Z"/>` +
        `<path class="${colour}" d="${head}"/><path class="hgf-fl-c" d="${circle(lean, -h, petal * 0.75)}"/></g>`,
      amp: 3 + hash(k, 1413) * 1.5,
      period: 3.2 + hash(k, 1415) * 1.6,
      phase: hash(k, 1417) * Math.PI * 2,
      gustAmp: 11
    });
  }

  const leaf = Math.min(Math.max(H * 0.07, 1.8), 3);
  k0 = Math.floor(x0 / 140) - 1;
  k1 = Math.ceil(x1 / 140) + 1;
  for (let k = k0; k <= k1; k++) {
    if (hash(k, 1501) > 0.5) continue;
    const x = (k + 0.5 + (hash(k, 1503) - 0.5) * 0.8) * 140;
    const h = H * (0.24 + hash(k, 1505) * 0.16);
    tufts.push({
      x,
      h: h + leaf,
      markup: '<g class="hgf-day">' +
        `<path class="hgf-stem" d="M-0.4,2L-0.3,${f2(-h)}L0.3,${f2(-h)}L0.4,2Z"/>` +
        `<path class="hgf-clover" d="${circle(-leaf * 0.85, -h + leaf * 0.2, leaf) + circle(leaf * 0.85, -h + leaf * 0.2, leaf) + circle(0, -h - leaf * 0.75, leaf)}"/></g>`,
      amp: 2.5 + hash(k, 1507),
      period: 3 + hash(k, 1509) * 1.5,
      phase: hash(k, 1511) * Math.PI * 2,
      gustAmp: 7
    });
  }

  tufts.sort((a, b) => a.x - b.x);
  return tufts;
}

// The site's theme: night unless the page is explicitly light.
function readTone() {
  if (typeof document === 'undefined') return 'night';
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'day' : 'night';
}

export default {
  name: 'HeroGrassFringe',
  inject: ['heroState'],
  data() {
    return {
      settled: false,
      // The theme the grass is drawn in (see crossfade).
      tone: readTone()
    };
  },
  watch: {
    'heroState.width': 'queueMeasure',
    'heroState.height': 'queueMeasure',
    'heroState.running': 'syncLoop',
    'heroState.reducedMotion'(still) {
      if (!still) this.settled = true;
      this.syncLoop();
      this.render(this.clock);
    }
  },
  created() {
    // Plain, non-reactive state (the clock is the sum of frame deltas, in s).
    this.clock = 0;
    this.lastRender = 0;
    this.built = null;
    this.half = 0;
    this.box = '';
    this.tufts = [];
    this.ghosts = [];
    this.debugPaused = false;
  },
  mounted() {
    this.loop = createLoop((dt) => {
      this.clock += dt / 1000;
      if (!this.settled && this.clock > SETTLED_AT) this.settled = true;
      if (this.clock - this.lastRender >= STEADY_FRAME) this.render(this.clock);
    });
    this.measure();
    this.syncLoop();
    if (typeof MutationObserver !== 'undefined') {
      this.themeObserver = new MutationObserver(() => this.onTheme());
      this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    }
    exposeDebug('grassFringe', {
      pause: () => {
        this.debugPaused = true;
        this.syncLoop();
      },
      resume: () => {
        this.debugPaused = false;
        this.syncLoop();
      },
      renderAt: (ms) => {
        this.clock = ms / 1000;
        this.settled = this.clock > SETTLED_AT;
        this.render(this.clock);
      },
      time: () => Math.round(this.clock * 1000),
      stats: () => ({
        tufts: this.tufts.length,
        nodes: this.$el.querySelectorAll('*').length,
        ghosts: this.ghosts.length,
        built: this.built
      })
    });
  },
  beforeDestroy() {
    this.destroyed = true;
    this.loop.stop();
    if (this.themeObserver) this.themeObserver.disconnect();
    this.clearGhosts();
    removeDebug('grassFringe');
  },
  methods: {
    queueMeasure() {
      if (this.measureQueued) return;
      this.measureQueued = true;
      this.$nextTick(() => {
        this.measureQueued = false;
        if (!this.destroyed) this.measure();
      });
    },
    syncLoop() {
      if (this.heroState.running && !this.heroState.reducedMotion && !this.debugPaused) {
        this.loop.start();
      } else {
        this.loop.stop();
      }
    },
    // Tufts are laid out in px from the centre with a margin; they are only
    // regenerated when the width or the fringe height changes meaningfully.
    measure() {
      const width = this.$el.clientWidth;
      const height = this.$el.clientHeight;
      if (!width || !height) return;
      const half = (width + OVERSCAN * 2) / 2;
      const b = this.built;
      if (!b || half > b.range - 4 || half < b.half * 0.8 || Math.abs(height - b.height) / b.height > 0.06) {
        this.clearGhosts();
        const range = half * 1.15 + 40;
        this.tufts = buildTufts(-range, range, height);
        const svg = this.$refs.svg;
        svg.innerHTML = this.tufts.map((t) => `<g>${t.markup}</g>`).join('');
        this.tufts.forEach((t, i) => {
          t.el = svg.children[i];
          t.last = '';
          // The bend step that moves a tuft's tip ~0.2 px: a tuft is only
          // redrawn when its bend changes visibly.
          t.step = Math.min(Math.max((0.2 / Math.max(t.h, 1)) * (180 / Math.PI), 0.1), 0.6);
        });
        this.built = { half, range, height };
      }
      this.half = half;
      const box = `${f2(-half)} ${f2(-height)} ${f2(half * 2)} ${f2(height)}`;
      if (box !== this.box) {
        // A dusk snapshot would no longer line up with the new view.
        this.clearGhosts();
        this.box = box;
        this.$refs.svg.setAttribute('viewBox', box);
      }
      this.render(this.clock);
    },
    render(t) {
      if (!this.built) return;
      this.lastRender = t;
      const still = this.heroState.reducedMotion;
      const gust = still ? null : gustField(t);
      const breeze = breezeAt(t);
      const half = this.half;
      for (let i = 0; i < this.tufts.length; i++) {
        const tuft = this.tufts[i];
        let angle = 0;
        if (!still) {
          angle = tuft.amp * breeze * Math.sin((t * Math.PI * 2) / tuft.period + tuft.phase) +
            gust.at((tuft.x + half) / (half * 2)) * tuft.gustAmp;
        }
        // skewX bends the tuft from its root (positive angle leans right),
        // quantised to the tuft's own step.
        const value = `translate(${f2(tuft.x)} 0) skewX(${f2(-Math.round(angle / tuft.step) * tuft.step)})`;
        if (value !== tuft.last) {
          tuft.last = value;
          tuft.el.setAttribute('transform', value);
        }
      }
    },
    onTheme() {
      const next = readTone();
      if (next === this.tone) return;
      this.crossfade(FADE_DELAY[next]);
      // The live grass switches at once (its tone class is patched before
      // the next frame), under the snapshot taken just above.
      this.tone = next;
    },
    // Dusk and dawn: transitioning the colour of hundreds of blades would
    // restyle them all on every frame of the fade. Instead the grass
    // switches to the new colours at once, under a snapshot of itself in the
    // old colours (it keeps its old tone class) that fades out on its own
    // compositing layer. The day flowers also fade on the live layer (see
    // .hgf-day), so they never pop in where the snapshot doesn't cover them.
    crossfade(delay) {
      if (!this.built || typeof Element === 'undefined' || typeof Element.prototype.animate !== 'function') return;
      if (this.ghosts.length >= MAX_GHOSTS) this.dropGhost(this.ghosts[0], true);
      const live = this.$refs.svg;
      const ghost = live.cloneNode(true);
      ghost.setAttribute('class', ghost.getAttribute('class') + ' hgf-ghost');
      // Right above the live grass, below any older snapshot still fading,
      // so a quick toggle back carries on from what is on screen.
      live.parentNode.insertBefore(ghost, live.nextSibling);
      const anim = ghost.animate([{ opacity: 1 }, { opacity: 0 }], {
        duration: FADE_MS,
        delay,
        easing: FADE_EASE,
        fill: 'both'
      });
      const entry = { el: ghost, anim };
      anim.onfinish = () => this.dropGhost(entry, false);
      this.ghosts.push(entry);
    },
    dropGhost(entry, cancel) {
      const i = this.ghosts.indexOf(entry);
      if (i >= 0) this.ghosts.splice(i, 1);
      entry.anim.onfinish = null;
      if (cancel) entry.anim.cancel();
      if (entry.el.parentNode) entry.el.parentNode.removeChild(entry.el);
    },
    clearGhosts() {
      if (!this.ghosts.length) return;
      this.ghosts.slice().forEach((entry) => this.dropGhost(entry, true));
    }
  }
}
</script>

<style scoped lang="scss">
// Colour tokens, set by the SVG's tone class (the theme switches there at
// once, under a fading snapshot: see crossfade). Closer to the camera than
// the meadow: darker and more saturated.
$hgf-night: (
  a: #0a1d0f,
  b: #0f2a16,
  c: #1b4224,
  stem: #0f2a16,
  clover: #12351b
);

$hgf-day: (
  a: #4b8b3b,
  b: #5f9e46,
  c: #7fc157,
  stem: #5a9842,
  clover: #4f9442
);

// Same timing as the sky and the landscape (HeroSky.vue, HeroLandscape.vue).
$hgf-ease: cubic-bezier(0.45, 0.05, 0.55, 0.95);
$hgf-theme-t: 1.4s;

.hero-grass-fringe {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: calc(var(--hero-ground-h) * 0.85);
  pointer-events: none;
}

// The 3D translation already gives the grass its own compositing layer.
.hgf-par {
  position: absolute;
  top: 0;
  bottom: 0;
  left: -24px;
  right: -24px;
  transform: translate3d(calc(var(--hero-px, 0) * 14px), 0, 0);
}

// Rises with the meadow on entrance; the animation is dropped once over.
.hgf-rise {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  animation: hgf-rise 1.1s cubic-bezier(0.16, 1, 0.3, 1) 0.75s both;
}

.is-settled .hgf-rise {
  animation: none;
}

@keyframes hgf-rise {
  from { transform: translate3d(0, 100%, 0); }
  to { transform: translate3d(0, 0, 0); }
}

.hgf-svg {
  position: absolute;
  left: 0;
  top: 0;
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.hgf-tone-night {
  @each $name, $value in $hgf-night {
    --hgf-#{$name}: #{$value};
  }
}

.hgf-tone-day {
  @each $name, $value in $hgf-day {
    --hgf-#{$name}: #{$value};
  }
}

// A dusk / dawn snapshot, fading out (see crossfade).
.hgf-svg.hgf-ghost {
  will-change: opacity;
}

.hero-grass-fringe ::v-deep {
  .hgf-a { fill: var(--hgf-a); }
  .hgf-b { fill: var(--hgf-b); }
  .hgf-c { fill: var(--hgf-c); }
  .hgf-stem { fill: var(--hgf-stem); }
  .hgf-clover { fill: var(--hgf-clover); }
  .hgf-fl-w { fill: #fff8e1; }
  .hgf-fl-y { fill: #ffd36e; }
  .hgf-fl-p { fill: #f6a6b8; }
  .hgf-fl-c { fill: #efb441; }

  // Flowers and clover belong to the day.
  .hgf-day {
    opacity: 0;
    transition: opacity $hgf-theme-t $hgf-ease 0.2s;
  }

  .hgf-tone-day .hgf-day {
    opacity: 1;
    transition-delay: 0.1s;
  }
}

.is-still .hgf-rise {
  animation: none;
}

@media (prefers-reduced-motion: reduce) {
  .hgf-rise {
    animation: none;
  }
}
</style>
